import Axios, { AxiosRequestConfig } from 'axios';
import {
  getMockCouponDetail,
  getMockCoupons,
  getMockExperienceCouponList,
  getMockExperienceOpen,
  getMockExperienceOrderPage,
  getMockExperiencePreview,
  getMockReceiveResult,
  getMockRewardHistory,
  getMockTaskDetail,
  getMockTasks,
} from './mockData';
import { CouponFilterType, CouponTab, TaskBelong } from './types';

/**
 * Mock 开关：
 * - 默认关闭，走正式接口
 * - 需要本地 mock 时设 REACT_APP_WELFARE_MOCK=true
 * - URL 添加 welfareMock=1/0 可运行时覆盖
 */
export const isWelfareMockEnabled = (() => {
  if (typeof window !== 'undefined') {
    const value = new URLSearchParams(window.location.search).get('welfareMock');
    if (value !== null) return value === '1' || value === 'true';
  }
  return String(process.env.REACT_APP_WELFARE_MOCK ?? 'false') === 'true';
})();

const mockDelay = <T>(data: T) =>
  new Promise<T>(resolve => {
    window.setTimeout(() => resolve(data), 250);
  });

const getMockResponse = (url: string, config?: AxiosRequestConfig) => {
  const params = config?.params || {};
  if (url.includes('/task/list')) {
    return getMockTasks(params.taskBelong as TaskBelong);
  }
  if (url.includes('/task/detail')) return getMockTaskDetail(Number(params.taskId));
  if (url.includes('/coupon/summary')) {
    // 汇总：已领取成功/使用中面额之和（mock 用 USED 列表近似）
    return {
      activeAmount:
        getMockCoupons('USED').reduce((sum, item) => sum + Number(item.amount || 0), 0) || 100,
    };
  }
  if (url.includes('/experience-position/order-page')) {
    return getMockExperienceOrderPage(Number(params.symbolId || 1), Number(params.userCouponId || 1011));
  }
  if (url.includes('/experience-position/coupon/list')) {
    return getMockExperienceCouponList();
  }
  // Step 28 查询（entrust/order/position）不走 mock，筛选只传参打真实接口
  if (url.includes('/user/coupon/list')) {
    const items = getMockCoupons(
      params.tab as CouponTab,
      (params.couponFamily) as CouponFilterType,
    );
    return { page: Number(params.page || 1), ps: items.length, total: items.length, items };
  }
  if (url.includes('/coupon/detail')) {
    return getMockCouponDetail(Number(params.userCouponId));
  }
  if (url.includes('/reward/history')) {
    return getMockRewardHistory((params.couponFamily) as CouponFilterType);
  }
  return undefined;
};

const postMockResponse = (url: string, data?: any) => {
  if (url.includes('/coupon/receive')) {
    return getMockReceiveResult(Number(data?.userCouponId), {
      confirmConflict: Boolean(data?.confirmConflict ?? data?.confirmRateConflict),
      confirmToken: data?.confirmToken,
    });
  }
  if (url.includes('/experience-position/open/preview')) {
    return getMockExperiencePreview(data);
  }
  if (url.includes('/experience-position/open') && !url.includes('preview')) {
    return getMockExperienceOpen();
  }
  if (url.includes('/experience-position/close')) {
    return {
      resultStatus: 'SUCCESS',
      resultCode: 'SUCCESS',
      positionId: data?.positionId,
      idempotentReplay: false,
    };
  }
  if (url.includes('/experience-position/entrust/')) {
    return {
      active: !url.includes('cancel'),
      canceled: url.includes('cancel'),
      entrustId: Date.now(),
      entrustStatus: url.includes('cancel') ? 'CANCELED' : 'ACTIVE',
      resultCode: 'SUCCESS',
    };
  }
  return undefined;
};

/** 福利中心与其它用户接口一致，走 /next/f/u/... */
export const welfareGet = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  if (isWelfareMockEnabled) {
    const mock = getMockResponse(url, config);
    if (mock !== undefined) return mockDelay(mock as T);
  }
  return Axios.get<any, T>(url, config) as any;
};

export const welfarePost = <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
  if (isWelfareMockEnabled) {
    const mock = postMockResponse(url, data);
    if (mock !== undefined) return mockDelay(mock as T);
  }
  // 福利中心 POST 一律 JSON；未指定 Content-Type 时 axios 会 qs.stringify 成 form，后端返回 415
  return Axios.post<any, T>(url, data, {
    ...config,
    headers: {
      'Content-Type': 'application/json',
      ...(config?.headers || {}),
    },
  }) as any;
};
